/**
 * At the limit — shown the moment "New puzzle" is tapped with every slot in use,
 * before anything is written. The old flow said nothing until the end, where the
 * finish button quietly turned into "Save as draft".
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../chassis/Icon";
import ThemeArt from "../themeArt";
import { Heading, PrimaryButton, Sub } from "../chassis/widgets";
import { C, FONT, GRADIENT, SHADOW } from "../chassis/tokens";

export default function LimitScreen({ puzzles, limit, onDelete, onUpgrade, onBuildAnyway, onClose }) {
  const { t: tr } = useTranslation();
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  async function remove(entry) {
    setBusy(true);
    try {
      await onDelete(entry);
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="creator-limit"
      style={{ minHeight: "100dvh", boxSizing: "border-box", position: "relative", overflow: "hidden", background: GRADIENT.page, color: C.ink, fontFamily: FONT, display: "flex", flexDirection: "column" }}
    >
      <div aria-hidden="true" style={{ position: "absolute", top: -110, right: -130, width: 330, height: 330, borderRadius: 999, background: C.blob1, filter: "blur(66px)", opacity: 0.75 }} />

      <header style={{ position: "relative", zIndex: 2, height: "calc(env(safe-area-inset-top, 0px) + 64px)", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "0 16px", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <button type="button" aria-label={tr("app.creator.nav.close")} onClick={onClose} style={{ width: 44, height: 44, borderRadius: 999, border: "none", background: C.surface, boxShadow: SHADOW.raised, display: "grid", placeItems: "center", cursor: "pointer" }}>
          <Icon name="close" size={16} color={C.ink} strokeWidth={2.4} />
        </button>
      </header>

      <main style={{ position: "relative", zIndex: 1, flexGrow: 1, overflowY: "auto", padding: "16px 24px 0" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {Array.from({ length: Math.min(limit, 10) }, (_, i) => (
            <span key={i} style={{ flexGrow: 1, height: 10, borderRadius: 5, background: GRADIENT.primary }} />
          ))}
        </div>
        <div style={{ marginTop: 9, fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent }}>
          {tr("app.creator.limit.eyebrow", { used: limit, limit })}
        </div>

        <Heading size={32} accent={tr("app.creator.limit.headingAccent")}>{tr("app.creator.limit.heading")}</Heading>
        <Sub>{tr("app.creator.limit.sub")}</Sub>

        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {puzzles.map((p) => (
            <div key={p.puzzleId} style={{ display: "flex", alignItems: "center", gap: 14, padding: 12, borderRadius: 20, background: C.surface, boxShadow: SHADOW.card }}>
              <span style={{ flexShrink: 0, width: 48, height: 48, borderRadius: 14, overflow: "hidden", display: "grid", placeItems: "center", transform: "scale(0.52)", transformOrigin: "center" }}>
                <ThemeArt theme={p.theme} />
              </span>
              <span style={{ flexGrow: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 800, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.title || tr("app.creator.limit.untitled")}
                </span>
                <span style={{ display: "block", marginTop: 2, fontSize: 12, color: C.soft }}>
                  {tr("app.creator.limit.meta", { clues: p.levelCount || 0 })}
                </span>
              </span>
              {confirming === p.puzzleId ? (
                <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button type="button" disabled={busy} onClick={() => remove(p)} data-testid={`creator-limit-confirm-${p.puzzleId}`} style={{ height: 40, padding: "0 12px", borderRadius: 12, border: "none", background: GRADIENT.primary, color: C.white, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {busy ? tr("app.creator.limit.deleting") : tr("app.creator.limit.confirm")}
                  </button>
                  <button type="button" onClick={() => setConfirming(null)} style={{ height: 40, padding: "0 12px", borderRadius: 12, border: "none", background: C.surfaceAlt, color: C.body, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {tr("app.creator.common.cancel")}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={tr("app.creator.limit.delete", { title: p.title || "" })}
                  data-testid={`creator-limit-delete-${p.puzzleId}`}
                  onClick={() => setConfirming(p.puzzleId)}
                  style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 14, border: "none", background: C.accentSoft, display: "grid", placeItems: "center", cursor: "pointer" }}
                >
                  <Icon name="trash" size={17} color={C.accentDeep} strokeWidth={2.1} />
                </button>
              )}
            </div>
          ))}
        </div>

        <p style={{ margin: "14px 4px 0", fontSize: 12, lineHeight: 1.5, color: C.faint }}>{tr("app.creator.limit.note")}</p>
      </main>

      <div style={{ position: "relative", zIndex: 2, padding: "10px 20px calc(env(safe-area-inset-bottom, 0px) + 22px)" }}>
        <PrimaryButton testId="creator-limit-upgrade" onClick={onUpgrade} icon={null}>
          {tr("app.creator.limit.upgrade")}
        </PrimaryButton>
        <button
          type="button"
          data-testid="creator-limit-build"
          onClick={onBuildAnyway}
          style={{ display: "block", width: "100%", marginTop: 14, border: "none", background: "transparent", textAlign: "center", fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.body, cursor: "pointer" }}
        >
          {tr("app.creator.limit.buildAnyway")}
        </button>
      </div>
    </div>
  );
}
